import { TrendingUp, Wallet, Receipt, Scale } from "lucide-react";
import { Kpi } from "../common";
import { eur2 as eur, pctText, signTone } from "../../lib/format";
import { cn } from "../../lib/utils";
import { SiteCostDonut } from "./CostCharts";

const Line = ({ label, value, strong, tone, testid }) => (
  <div className={cn("flex justify-between gap-3 py-1.5 border-b border-border/40 text-sm", strong && "font-semibold")}>
    <span className="text-muted-foreground">{label}</span><span className={cn("num", tone)} data-testid={testid}>{eur(value)}</span>
  </div>
);

const Block = ({ title, children }) => (
  <div className="surface p-4"><div className="eyebrow mb-2">{title}</div>{children}</div>
);

const tone = (n) => signTone(n, "text-red-400", "text-emerald-300");

export default function FinanceSummary({ s }) {
  const pct = pctText(s.margin_pct);
  return (
    <div className="space-y-4" data-testid="fin-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Valore cantiere" value={eur(s.contract_value)} icon={TrendingUp} hint={s.value_source === "budget" ? "Budget del cantiere (manca il preventivo)" : "Preventivo + varianti approvate"} testid="fin-kpi-contract" />
        <Kpi label="Costi totali" value={eur(s.total_costs)} icon={Receipt} accent="bg-orange-500" testid="fin-kpi-costs" />
        <Kpi label="Margine stimato" value={eur(s.margin)} icon={Scale} accent={signTone(s.margin, "bg-red-500", "bg-emerald-500") || "bg-sky-500"} hint={`Margine ${pct}`} testid="fin-kpi-margin" />
        <Kpi label="Saldo finanziario" value={eur(s.cash_balance)} icon={Wallet} accent="bg-sky-500" hint="Incassato − pagato fornitori" testid="fin-kpi-cash" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Block title="Ricavi">
          <Line label={s.value_source === "budget" ? "Budget (nessun preventivo)" : "Preventivo iniziale"} value={s.initial_quote} testid="fin-initial-quote" />
          <Line label="Varianti approvate" value={s.variations_approved} testid="fin-variations-approved" />
          <Line label="Varianti in attesa (non conteggiate)" value={s.variations_pending} />
          <Line label="Valore complessivo" value={s.contract_value} strong />
        </Block>
        <Block title="Costi">
          <Line label="Acquisti materiali" value={s.materials} testid="fin-materials" />
          <Line label="Spese extra / imprevisti" value={s.extras} testid="fin-extras" />
          {Object.entries(s.extras_by_category).map(([k, v]) => <Line key={k} label={`  · ${k}`} value={v} />)}
          <Line label="Manodopera pianificata" value={s.labor_cost} />
          <Line label="Mezzi pianificati" value={s.vehicle_cost} />
          <Line label="Totale costi" value={s.total_costs} strong />
        </Block>
        <Block title="Pagamenti e incassi">
          <Line label="Fatturato / richiesto al cliente" value={s.invoiced} />
          <Line label="Incassato" value={s.received} tone="text-emerald-300" testid="fin-received" />
          <Line label="Credito residuo su fatture" value={s.credit_open} tone="text-amber-300" testid="fin-credit-open" />
          <Line label="Da incassare sul valore cantiere" value={s.to_collect} />
          <Line label="Pagato ai fornitori" value={s.supplier_paid} />
          <Line label="Da pagare ai fornitori" value={s.supplier_due} tone="text-amber-300" testid="fin-supplier-due" />
          <Line label="Saldo finanziario" value={s.cash_balance} strong tone={tone(s.cash_balance)} />
        </Block>
      </div>
      <SiteCostDonut byCat={s.costs_by_category} />
    </div>
  );
}
